<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  <form [formGroup]="userForm" (ngSubmit)="onSubmit()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center gap-3">
            <p-button
              icon="pi pi-arrow-left"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              type="button"
              (onClick)="cancel()"
              pTooltip="Volver al listado"
              tooltipPosition="bottom"
            />
            <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <i class="pi pi-users"></i>
              </span>
              <span class="truncate">{{ isEditMode() ? 'Editar usuario' : 'Nuevo usuario' }}</span>
            </h2>
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="inline-flex items-baseline gap-x-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
            <span class="text-[11px] font-semibold tracking-wider text-slate-500">STATUS:</span>
            <span class="text-sm font-medium text-slate-900 font-mono">{{ statusDisplayLabel() }}</span>
            <i
              class="pi pi-question-circle text-slate-400 cursor-help text-xs shrink-0"
              [pTooltip]="statusTooltip"
              tooltipPosition="bottom"
            ></i>
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
          @for (tab of tabs; track tab.value) {
            <p-tab
              [value]="tab.value"
              [disabled]="!isEditMode() && tab.value !== 'generales'"
              class="flex items-center gap-2! text-inherit"
            >
              <i [class]="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </p-tab>
          }
        </p-tablist>
        <p-tabpanels>
          <!-- Tab Generales -->
          <p-tabpanel value="generales">
            <div class="mt-4 mb-4">
              <h3 class="text-lg font-semibold text-slate-900">Información del usuario</h3>
              <p class="text-sm text-slate-500">Datos personales del usuario.</p>
            </div>

            <div class="mb-8">
              <h4 class="text-sm font-semibold text-slate-700 mb-1">Foto de perfil</h4>
              <p class="text-xs text-slate-500 mb-3">Sube una imagen, ajústala en el círculo y se guardará al enviar el formulario.</p>
              <app-avatar-uploader
                [avatarUrl]="avatarPreviewUrl()"
                [disabled]="loading()"
                [buttonLabel]="avatarPreviewUrl() ? 'Cambiar foto' : 'Subir foto'"
                (avatarChange)="onAvatarChange($event)"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <!-- Nombre -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="name"
                    formControlName="name"
                    [maxlength]="50"
                    fluid
                    autocomplete="off"
                    [invalid]="(userForm.get('name')?.invalid ?? false) && (userForm.get('name')?.touched ?? false)"
                  />
                  <label for="name">Nombre</label>
                </p-floatlabel>
                @if (userForm.get('name')?.hasError('required') && userForm.get('name')?.touched) {
                  <small class="text-red-600">El nombre es requerido</small>
                }
                @if (userForm.get('name')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 50 caracteres</small>
                }
              </div>

              <!-- Apellido -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="lastname"
                    formControlName="lastname"
                    [maxlength]="30"
                    fluid
                    autocomplete="off"
                    [invalid]="(userForm.get('lastname')?.invalid ?? false) && (userForm.get('lastname')?.touched ?? false)"
                  />
                  <label for="lastname">Apellido</label>
                </p-floatlabel>
                @if (userForm.get('lastname')?.hasError('required') && userForm.get('lastname')?.touched) {
                  <small class="text-red-600">El apellido es requerido</small>
                }
                @if (userForm.get('lastname')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 30 caracteres</small>
                }
              </div>

              <!-- Apellido segundo -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="surname"
                    formControlName="surname"
                    [maxlength]="30"
                    fluid
                    autocomplete="off"
                  />
                  <label for="surname">Apellido segundo</label>
                </p-floatlabel>
                @if (userForm.get('surname')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 30 caracteres</small>
                }
              </div>

              <!-- Cargo -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="jobTitle"
                    formControlName="jobTitle"
                    [maxlength]="150"
                    fluid
                    autocomplete="off"
                    [invalid]="(userForm.get('jobTitle')?.invalid ?? false) && (userForm.get('jobTitle')?.touched ?? false)"
                  />
                  <label for="jobTitle">Cargo</label>
                </p-floatlabel>
                @if (userForm.get('jobTitle')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 150 caracteres</small>
                }
              </div>

              <!-- Teléfono -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="phone"
                    formControlName="phone"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    [maxlength]="10"
                    fluid
                    autocomplete="off"
                    (input)="onPhoneInput($event)"
                    [invalid]="(userForm.get('phone')?.invalid ?? false) && (userForm.get('phone')?.touched ?? false)"
                  />
                  <label for="phone">Teléfono</label>
                </p-floatlabel>
                @if (userForm.get('phone')?.hasError('pattern') && userForm.get('phone')?.touched) {
                  <small class="text-red-600">Debe tener exactamente 10 dígitos</small>
                }
              </div>

              <!-- Email: editable en creación, solo lectura en edición -->
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input
                    pInputText
                    id="email"
                    type="email"
                    formControlName="email"
                    fluid
                    autocomplete="off"
                    [readonly]="isEditMode()"
                    [invalid]="(userForm.get('email')?.invalid ?? false) && (userForm.get('email')?.touched ?? false)"
                    [class.!bg-slate-100]="isEditMode()"
                    [class.cursor-not-allowed]="isEditMode()"
                  />
                  <label for="email">{{ isEditMode() ? 'Email (solo lectura)' : 'Email' }}</label>
                </p-floatlabel>
                @if (!isEditMode()) {
                  @if (userForm.get('email')?.hasError('required') && userForm.get('email')?.touched) {
                    <small class="text-red-600">El email es requerido</small>
                  }
                  @if (userForm.get('email')?.hasError('email') && userForm.get('email')?.touched) {
                    <small class="text-red-600">Email inválido</small>
                  }
                }
              </div>

              <!-- Contraseña: solo en creación -->
              @if (!isEditMode()) {
                <div class="flex flex-col gap-1 md:col-span-2">
                  <div class="flex items-stretch gap-2 w-full">
                    <p-floatlabel class="flex-1 min-w-0" variant="on">
                      <p-inputgroup class="w-full">
                        <input
                          pInputText
                          id="password"
                          [type]="passwordVisible() ? 'text' : 'password'"
                          formControlName="password"
                          [maxlength]="50"
                          fluid
                          autocomplete="new-password"
                        />
                        <p-inputgroup-addon>
                          <button
                            type="button"
                            class="p-0 border-0 bg-transparent cursor-pointer text-slate-500 hover:text-slate-700"
                            (click)="togglePasswordVisible()"
                            [attr.aria-label]="passwordVisible() ? 'Ocultar contraseña' : 'Ver contraseña'"
                          >
                            <i [class]="passwordVisible() ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                          </button>
                        </p-inputgroup-addon>
                      </p-inputgroup>
                      <label for="password">Contraseña</label>
                    </p-floatlabel>
                    <p-button
                      label="Generar"
                      icon="pi pi-refresh"
                      severity="secondary"
                      (onClick)="generatePassword()"
                      type="button"
                      class="shrink-0"
                    />
                  </div>
                  <small class="text-slate-500">Opcional. Puedes generar una aleatoria o dejarla en blanco y configurarla después en Keycloak.</small>
                </div>
              }
            </div>
          </p-tabpanel>

          <!-- Tab Permisos (solo edición) -->
          @if (isEditMode()) {
            <p-tabpanel value="permisos">
              <div class="mt-4 mb-4">
                <h3 class="text-lg font-semibold text-slate-900">Permisos del usuario</h3>
                <p class="text-sm text-slate-500">Selecciona los permisos asignados a este usuario.</p>
              </div>
              @if (currentUserType() === 'OWNER') {
                <div class="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  Los permisos de los usuarios tipo Propietario no se pueden modificar.
                </div>
              } @else if (permissionsLoading()) {
                <div class="flex items-center gap-2 py-8">
                  <i class="pi pi-spin pi-spinner text-xl text-slate-500"></i>
                  <span class="text-slate-600">Cargando permisos...</span>
                </div>
              } @else {
                <div class="mt-4 overflow-y-auto space-y-6">
                  @for (group of permissionsByContext(); track group.context) {
                    <div>
                      <h4 class="text-sm font-semibold text-slate-700 mb-2 pb-1 border-b border-slate-200">{{ group.label }}</h4>
                      <div class="space-y-1">
                        @for (perm of group.permissions; track perm.value) {
                          <div
                            class="flex items-center gap-3 py-2 px-2 rounded hover:bg-slate-50 cursor-pointer"
                            (click)="togglePermission(perm)"
                          >
                            <p-checkbox
                              [binary]="true"
                              [ngModel]="perm.isAssigned"
                              [ngModelOptions]="{standalone: true}"
                              (ngModelChange)="setPermissionAssigned(perm, $event)"
                              (click)="$event.stopPropagation()"
                            />
                            <div class="flex-1 min-w-0 flex items-center gap-2">
                              <span class="font-medium text-slate-900">{{ perm.value }}</span>
                              <span class="text-sm text-slate-500 truncate">{{ perm.description }}</span>
                              @if (perm.global) {
                                <span class="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded shrink-0">Global</span>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
                @if (permissionsList().length === 0 && !permissionsLoading()) {
                  <p class="text-slate-500 py-4">No hay permisos disponibles.</p>
                }
              }
            </p-tabpanel>

            <!-- Tab Roles (solo edición) -->
            <p-tabpanel value="roles">
              <div class="mt-4 mb-4">
                <h3 class="text-lg font-semibold text-slate-900">Roles del usuario</h3>
                <p class="text-sm text-slate-500">Selecciona los roles asignados. Cada rol concede los permisos indicados.</p>
              </div>
              @if (currentUserType() === 'OWNER') {
                <div class="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  Los roles de los usuarios tipo Propietario no se pueden modificar.
                </div>
              } @else if (rolesLoading()) {
                <div class="flex items-center gap-2 py-8">
                  <i class="pi pi-spin pi-spinner text-xl text-slate-500"></i>
                  <span class="text-slate-600">Cargando roles...</span>
                </div>
              } @else {
                <div class="mt-4 overflow-y-auto space-y-6">
                  @for (group of rolesByGroup(); track group.label) {
                    <div>
                      <h4 class="text-sm font-semibold text-slate-700 mb-2 pb-1 border-b border-slate-200">{{ group.label }}</h4>
                      <div class="space-y-3">
                        @for (role of group.roles; track role.value) {
                          <div
                            class="flex items-start gap-3 py-2 px-2 rounded hover:bg-slate-50 cursor-pointer border border-slate-100"
                            (click)="toggleRole(role)"
                          >
                            <p-checkbox
                              [binary]="true"
                              [ngModel]="role.isAssigned"
                              [ngModelOptions]="{standalone: true}"
                              (ngModelChange)="setRoleAssigned(role, $event)"
                              (click)="$event.stopPropagation()"
                              class="mt-1"
                            />
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2 flex-wrap">
                                <span class="font-medium text-slate-900">{{ role.value }}</span>
                                @if (role.global) {
                                  <span class="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded shrink-0">Global</span>
                                }
                                @if (role.system) {
                                  <span class="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded shrink-0">Sistema</span>
                                }
                              </div>
                              <p class="text-sm text-slate-600 mt-0.5">{{ role.description }}</p>
                              @if (role.permissions && role.permissions.length > 0) {
                                <button
                                  type="button"
                                  class="inline-flex items-center gap-1.5 mt-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded border-0 cursor-pointer"
                                  (click)="toggleRolePermissionsExpanded(role.value, $event)"
                                >
                                  <i class="pi text-slate-500 transition-transform duration-200" [class.pi-chevron-down]="isRolePermissionsExpanded(role.value)" [class.pi-chevron-right]="!isRolePermissionsExpanded(role.value)"></i>
                                  Permisos que concede ({{ role.permissions.length }})
                                </button>
                                @if (isRolePermissionsExpanded(role.value)) {
                                  <ul class="text-xs text-slate-600 list-disc list-inside mt-1.5 pl-1 space-y-0.5">
                                    @for (perm of role.permissions; track perm.value) {
                                      <li>{{ perm.value }} — {{ perm.description }}</li>
                                    }
                                  </ul>
                                }
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
                @if (rolesList().length === 0 && !rolesLoading()) {
                  <p class="text-slate-500 py-4">No hay roles disponibles.</p>
                }
              }
            </p-tabpanel>
          }

          <!-- Tab Contraseña -->
          <p-tabpanel value="password">
            <div class="mt-4 mb-4">
              <h3 class="text-lg font-semibold text-slate-900">Contraseña</h3>
              <p class="text-sm text-slate-500">Restablecer contraseña mediante Keycloak.</p>
            </div>
            @if (isEditMode()) {
              <div class="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <p class="text-sm text-slate-700 mb-2">Para restablecer la contraseña de este usuario, use el flujo de Keycloak.</p>
                <a
                  href="#"
                  (click)="openResetPasswordInKeycloak(); $event.preventDefault()"
                  class="inline-flex items-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800 hover:underline"
                >
                  <i class="pi pi-external-link"></i>
                  Abrir página para cambiar contraseña en Keycloak
                </a>
              </div>
            } @else {
              <p class="text-sm text-slate-600">El email y la contraseña se configuran en el tab <strong>Generales</strong> al crear un usuario.</p>
            }
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="User"
                [entityId]="editingUserId()"
                entityLabel="usuario"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div class="mt-4 flex justify-end gap-4 p-4 -m-5 bg-gray-50 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" [loading]="loading()" type="button" />
          <p-button
            [label]="loading() ? 'Guardando...' : (isEditMode() ? 'Actualizar Usuario' : 'Guardar Usuario')"
            [loading]="loading()"
            [disabled]="!canSave() || loading()"
            type="submit"
          />
        </div>
      </ng-template>
    </p-card>
  </form>
</div>
